"use client";

import { useMemo, useRef, useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import { KmSummary } from "@/components/tools/stats-clinical/km-results";
import { PasteBox } from "@/components/tools/stats-research/paste-box";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberList } from "@/lib/parse-number";
import {
  type IntervalType,
  kaplanMeier,
  logRank,
} from "@/lib/tool-kit/stats-clinical/kaplan-meier";
import {
  groupObservations,
  guessEventLabel,
  readSurvivalRows,
} from "@/lib/tool-kit/stats-clinical/km-input";
import {
  type HeaderMode,
  parseLabelTable,
} from "@/lib/tool-kit/stats-research/matrix";

/** English strings. A translated content file overrides these through `ui`. */
export const KAPLAN_MEIER_CALCULATOR_STRINGS = {
  dataLabel:
    "Data — one subject per row: follow-up time, status, and (optionally) group",
  dataHint:
    "Paste two or three columns from a spreadsheet, or separate values with tabs, commas or spaces. Status holds two labels, such as 1 and 0 or Dead and Alive; say below which one means the event happened. Add a third column to compare groups. A header row is optional.",
  dataPlaceholder: "9\t1\tMaintained\n13\t0\tMaintained\n5\t1\tNonmaintained",
  exampleHeader: "Weeks\tRelapse\tTreatment",
  exampleGroupA: "Maintained",
  exampleGroupB: "Nonmaintained",
  exampleUnit: "weeks",
  loadExample: "Load example",
  clear: "Clear",
  headerLabel: "Header row",
  headerAuto: "Detect automatically",
  headerYes: "First row is a header",
  headerNo: "No header row",
  eventLabel: "Which status means the event happened?",
  intervalLabel: "Confidence interval",
  intervalLogLog: "Log-log (stays within 0 to 1)",
  intervalLog: "Log (the default in R's survfit)",
  intervalPlain: "Plain (linear, clamped to 0 to 1)",
  unitLabel: "Time unit (optional)",
  timesLabel: "Survival at specific times (optional)",
  timesHint: "Times separated by commas, for example 12, 24, 60.",
  showBands: "Shade the confidence bands",
  showCensored: "Mark censored times on the curves",
  subjectsRead: "one: {count} subject|other: {count} subjects",
  groupsRead: "one: {count} group|other: {count} groups",
  droppedRows:
    "one: {count} row with a missing value was left out.|other: {count} rows with a missing value were left out.",
  unreadable:
    "Line {line}: “{text}” is not a valid follow-up time (it must be a number, zero or more).",
  needTwoStatuses:
    "The status column must hold two different labels (event and censored); this one holds {count}.",
  tooManyGroups: "At most six groups can be compared; this data has {count}.",
  allSubjects: "All subjects",
  enterPrompt:
    "Paste follow-up times with an event indicator to draw the Kaplan–Meier curve.",
  results: "Results",
  plotLabel: "Kaplan–Meier survival curves for {groups} group(s)",
  xAxis: "Time",
  yAxis: "Survival probability",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  downloadCsv: "Download survival table (CSV)",
  cardSubjects: "Subjects",
  cardEvents: "Events",
  cardMedian: "Median survival",
  cardP: "Log-rank p",
  columnGroup: "Group",
  columnN: "N",
  columnEvents: "Events",
  columnCensored: "Censored",
  columnMedian: "Median",
  columnMedianCi: "95% CI of the median",
  notReached: "not reached",
  beyondFollowUp: "beyond follow-up",
  logRankHeading: "Log-rank test",
  logRankText:
    "χ² = {chi} on {df} degrees of freedom, p {p}. The null hypothesis is that the groups share one survival curve.",
  logRankNote:
    "Observed and expected deaths are summed over every event time. O/E above 1 means more deaths than the pooled curve predicts. The log-rank test weights all event times equally; it has most power when the hazards are proportional.",
  columnObserved: "Observed",
  columnExpected: "Expected",
  columnOe: "O / E",
  riskHeading: "Number at risk",
  atTimesHeading: "Survival at your times",
  columnTime: "Time",
  columnAtRisk: "At risk",
  columnSurvival: "Survival",
  columnSe: "SE (Greenwood)",
  columnInterval: "95% CI",
  tableHeading: "Survival table",
  tableGroup: "Group shown",
  tableMore:
    "Showing the first {shown} of {total} times; the CSV has them all.",
  howCalculated: "How this was calculated",
  workingKm:
    "S(t) = Π (1 − dᵢ / nᵢ) over event times up to t, where dᵢ is the events and nᵢ the subjects at risk at time tᵢ.",
  workingGreenwood:
    "Greenwood: Var[ln S(t)] = Σ dᵢ / (nᵢ (nᵢ − dᵢ)); SE = S(t) × √Var[ln S(t)].",
  workingNote:
    "Intervals use the {interval} transformation at the 95% level. The median is the first time S(t) reaches 0.5 or below (the midpoint when it stops exactly on 0.5); its limits are the first times the interval curves reach 0.5. These are the conventions of R's survival package.",
  reportSingle:
    "Kaplan–Meier analysis: {n} subjects, {events} events; median survival {median} (95% CI {lower} to {upper}).",
  reportGroups:
    "Log-rank test across the groups: χ²({df}) = {chi}, p {p} (N = {n}).",
  copyResult: "Copy result",
  copied: "Copied!",
} as const;

const MAX_GROUPS = 6;

const INTERVAL_NAMES: Record<
  IntervalType,
  keyof typeof KAPLAN_MEIER_CALCULATOR_STRINGS
> = {
  "log-log": "intervalLogLog",
  log: "intervalLog",
  plain: "intervalPlain",
};

const EXAMPLE_TIME = [
  9, 13, 13, 18, 23, 28, 31, 34, 45, 48, 161, 5, 5, 8, 8, 12, 16, 23, 27, 30,
  33, 43, 45,
];
const EXAMPLE_STATUS = [
  1, 1, 0, 1, 1, 0, 1, 1, 0, 1, 0, 1, 1, 1, 1, 1, 0, 1, 1, 1, 1, 1, 1,
];
const EXAMPLE_FIRST_GROUP = 11;

export default function KaplanMeierCalculatorWidget() {
  const strings = useToolUi(KAPLAN_MEIER_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [text, setText] = useState("");
  const [header, setHeader] = useState<HeaderMode>("auto");
  const [chosenEvent, setChosenEvent] = useState("");
  const [intervalType, setIntervalType] = useState<IntervalType>("log-log");
  const [unit, setUnit] = useState("");
  const [timesText, setTimesText] = useState("");
  const [bands, setBands] = useState(true);
  const [censored, setCensored] = useState(true);
  const [tableGroup, setTableGroup] = useState(0);

  const table = useMemo(() => parseLabelTable(text, header), [text, header]);
  const read = useMemo(
    () => readSurvivalRows(table.rows, table.lines),
    [table]
  );
  const statuses = [...new Set(read.statuses)];
  const eventLabel = statuses.includes(chosenEvent)
    ? chosenEvent
    : guessEventLabel(statuses);
  const entered = text.trim() !== "";

  const analysis = useMemo(() => {
    if (read.unreadable || statuses.length === 0 || statuses.length > 2) {
      return null;
    }
    const grouped = groupObservations(
      read.hasGroups ? read : { ...read, groups: read.groups.map(() => "") },
      eventLabel
    );
    if (grouped.groups.length > MAX_GROUPS) {
      return null;
    }
    const names = read.hasGroups ? grouped.names : [strings.allSubjects];
    return {
      names,
      groups: grouped.groups,
      curves: grouped.groups.map((g) => kaplanMeier(g, intervalType)),
      logRank: grouped.groups.length > 1 ? logRank(grouped.groups) : null,
    };
  }, [read, statuses.length, eventLabel, intervalType, strings.allSubjects]);

  let problem: string | null = null;
  if (read.unreadable) {
    problem = t(strings.unreadable, {
      line: fmt(read.unreadable.line),
      text: read.unreadable.text,
    });
  } else if (statuses.length > 2) {
    problem = t(strings.needTwoStatuses, { count: fmt(statuses.length) });
  } else if (read.hasGroups && new Set(read.groups).size > MAX_GROUPS) {
    problem = t(strings.tooManyGroups, {
      count: fmt(new Set(read.groups).size),
    });
  }
  const dropped = read.dropped;
  const subjectCount = read.times.length;

  return (
    <div className="space-y-6">
      <PasteBox
        actions={[
          {
            label: strings.loadExample,
            onClick: () => {
              const rows = EXAMPLE_TIME.map((time, i) =>
                [
                  time,
                  EXAMPLE_STATUS[i],
                  i < EXAMPLE_FIRST_GROUP
                    ? strings.exampleGroupA
                    : strings.exampleGroupB,
                ].join("\t")
              );
              setHeader("auto");
              setText([strings.exampleHeader, ...rows].join("\n"));
              setChosenEvent("");
              setUnit(strings.exampleUnit);
              setTableGroup(0);
            },
          },
          { label: strings.clear, onClick: () => setText("") },
        ]}
        header={{
          label: strings.headerLabel,
          value: header,
          onChange: setHeader,
          options: [
            { value: "auto", label: strings.headerAuto },
            { value: "yes", label: strings.headerYes },
            { value: "no", label: strings.headerNo },
          ],
        }}
        hint={strings.dataHint}
        id="km-data"
        label={strings.dataLabel}
        onChange={setText}
        placeholder={strings.dataPlaceholder}
        rows={8}
        value={text}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {statuses.length > 0 && statuses.length <= 2 ? (
          <SelectField
            id="km-event"
            label={strings.eventLabel}
            onChange={setChosenEvent}
            options={statuses.map((value) => ({ value, label: value }))}
            value={eventLabel}
          />
        ) : null}
        <SelectField
          id="km-interval"
          label={strings.intervalLabel}
          onChange={setIntervalType}
          options={(Object.keys(INTERVAL_NAMES) as IntervalType[]).map(
            (value) => ({
              value,
              label: strings[INTERVAL_NAMES[value]],
            })
          )}
          value={intervalType}
        />
        <TextField
          id="km-unit"
          label={strings.unitLabel}
          onChange={setUnit}
          value={unit}
        />
        <TextField
          hint={strings.timesHint}
          id="km-times"
          label={strings.timesLabel}
          onChange={setTimesText}
          value={timesText}
        />
      </div>
      <div className="flex flex-wrap gap-6">
        <Checkbox
          checked={bands}
          label={strings.showBands}
          onChange={setBands}
        />
        <Checkbox
          checked={censored}
          label={strings.showCensored}
          onChange={setCensored}
        />
      </div>
      {entered && subjectCount > 0 ? (
        <p className={HINT}>
          {[
            tPlural(strings.subjectsRead, locale, subjectCount, {
              count: fmt(subjectCount),
            }).trim(),
            analysis && analysis.names.length > 1
              ? tPlural(strings.groupsRead, locale, analysis.names.length, {
                  count: fmt(analysis.names.length),
                }).trim()
              : "",
            dropped > 0
              ? tPlural(strings.droppedRows, locale, dropped, {
                  count: fmt(dropped),
                }).trim()
              : "",
          ]
            .filter((part) => part !== "")
            .join(" · ")}
        </p>
      ) : null}
      {problem ? <ErrorNotice>{problem}</ErrorNotice> : null}
      {analysis && !problem ? (
        <KmSummary
          atTimes={parseNumberList(timesText).filter((v) => v >= 0)}
          data={analysis}
          fmt={fmt}
          intervalName={strings[INTERVAL_NAMES[intervalType]]
            .split(" (")[0]
            .toLowerCase()}
          onSelectGroup={setTableGroup}
          selectedGroup={Math.min(tableGroup, analysis.curves.length - 1)}
          showBands={bands}
          showCensored={censored}
          strings={strings}
          svgRef={svgRef}
          timeUnit={unit.trim()}
        />
      ) : null}
      {entered ? null : <Notice>{strings.enterPrompt}</Notice>}
    </div>
  );
}
